/**
 * Kiosk Settings Repository
 * Data abstraction layer with transformations between API and domain models
 */

import { KioskSettingsApi } from './kiosk-settings-api';
import { KioskSettingsDto, TIME_CONVERSION, ConnectedStatusEnum } from '../types/kiosk-settings-types';

/**
 * Repository for kiosk settings
 * Handles:
 * - API communication abstraction
 * - Data transformation (milliseconds ↔ seconds)
 * - Error handling
 */
export class KioskSettingsRepository {
  constructor(private api: KioskSettingsApi) {}

  /**
   * Fetch kiosk settings from API
   * Transforms milliseconds → seconds for time fields
   * @param operatorId - The operator ID
   * @returns Promise with transformed KioskSettingsDto
   */
  async getSettings(operatorId: number): Promise<KioskSettingsDto> {
    try {
      const apiData = await this.api.getSettings(operatorId);
      const transformed = this.transformApiToModel(apiData);

      return transformed;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Repository: Failed to fetch kiosk settings - ${errorMessage}`);
    }
  }

  /**
   * Update kiosk settings via API
   * Transforms seconds → milliseconds for time fields
   * @param operatorId - The operator ID
   * @param settings - Full settings object (matches Flutter API expectations)
   */
  async updateSettings(
    operatorId: number,
    settings: KioskSettingsDto
  ): Promise<void> {
    try {
      // Transform: seconds → milliseconds for API
      const apiData = this.transformModelToApi(settings);

      // Send full transformed object to API
      await this.api.updateSettings(operatorId, apiData);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Repository: Failed to update kiosk settings - ${errorMessage}`);
    }
  }

  /**
   * Parse a raw settings object (already JSON-decoded) into a KioskSettingsDto.
   * Used when settings are pre-loaded from kioskDetails (no API call needed).
   */
  parseRawSettings(raw: any, operatorId: number): KioskSettingsDto {
    return this.transformApiToModel({ ...raw, operatorId });
  }

  /**
   * Build a changed-only API payload for kiosk save (registerKiosk endpoint).
   * Matches Flutter's getChangedValues(_kioskInfoOperatorSettings, updateJson()!):
   * transforms both current and original to API format, returns only changed top-level keys.
   */
  buildChangedApiPayload(settings: KioskSettingsDto, originalSettings: KioskSettingsDto): any {
    const currentApi = this.transformModelToApi(settings);
    const originalApi = this.transformModelToApi(originalSettings);
    const changed: any = {};
    for (const key of Object.keys(currentApi)) {
      if (JSON.stringify(currentApi[key]) !== JSON.stringify(originalApi[key])) {
        changed[key] = currentApi[key];
      }
    }
    return changed;
  }

  /**
   * Transform API response: Convert milliseconds → seconds
   * Time fields in API are stored as milliseconds, UI displays as seconds
   * @private
   */
  private transformApiToModel(apiData: any): KioskSettingsDto {
    const model: KioskSettingsDto = {
      ...apiData,

      // API uses 'id', DTO uses 'operatorId'
      operatorId: apiData.operatorId ?? apiData.id ?? 0,

      // API uses 'logo', DTO uses 'logoUrl'
      // Only read the API/Flutter key — matching Flutter's KioskSettingsKeys.logoUrl = 'logo'
      logoUrl: apiData.logo ?? undefined,

      // API uses 'connected', DTO uses 'connectedStatus'
      // Only read the API/Flutter key — matching Flutter's KioskSettingsKeys.connectedStatus = 'connected'
      // Case-sensitive match, same as Flutter's firstWhereOrNull((e) => e.name == settings['connected'])
      // Flutter enum names: 'Blocked', 'Reserved' — only exact matches are valid
      connectedStatus: (() => {
        const raw = apiData.connected ?? null;
        if (!raw) return null;
        return Object.values(ConnectedStatusEnum).find(v => v === raw) ?? null;
      })(),

      // API uses 'minCostPerMinuteThreshold', DTO uses 'minimumCostPerMinute'
      // Only read the API/Flutter key — matching Flutter's KioskSettingsKeys.minimumCostPerMinute = 'minCostPerMinuteThreshold'
      minimumCostPerMinute: apiData.minCostPerMinuteThreshold ?? undefined,

      // API uses 'isSupportedIncrementalAuth', DTO uses 'isIncrementalAuth'
      // Only read the API/Flutter key — matching Flutter's KioskSettingsKeys.isIncrementalAuth = 'isSupportedIncrementalAuth'
      isIncrementalAuth: apiData.isSupportedIncrementalAuth ?? false,

      // API uses 'maxTransactionFeePercentage', DTO uses 'maxTransactionFee'
      // Only read the API/Flutter key — matching Flutter's KioskSettingsKeys.maxTransactionFee = 'maxTransactionFeePercentage'
      maxTransactionFee: apiData.maxTransactionFeePercentage ?? undefined,

      // Transform sessionTimeout: ms → seconds
      sessionTimeout: apiData.sessionTimeout
        ? Math.round(TIME_CONVERSION.MS_TO_SECONDS(apiData.sessionTimeout))
        : 60,

      // Transform idleScreen.timeout: ms → seconds
      idleScreen: {
        ...apiData.idleScreen,
        timeout: apiData.idleScreen?.timeout
          ? Math.round(TIME_CONVERSION.MS_TO_SECONDS(apiData.idleScreen.timeout))
          : 30
      },

      // Transform connectedAction.time: ms → seconds
      connectedAction: {
        ...apiData.connectedAction,
        time: apiData.connectedAction?.time
          ? Math.round(TIME_CONVERSION.MS_TO_SECONDS(apiData.connectedAction.time))
          : undefined
      },

      // Normalize language: API may return {code, name} object instead of string
      language: typeof apiData.language === 'object' && apiData.language !== null
        ? (apiData.language.code ?? '')
        : (apiData.language ?? ''),

      // Normalize supportedLanguages: API may return array of {code, name} objects
      supportedLanguages: Array.isArray(apiData.supportedLanguages)
        ? apiData.supportedLanguages.map((l: any) =>
            typeof l === 'object' && l !== null ? (l.code ?? l) : l
          )
        : (apiData.supportedLanguages ?? []),

      // API uses licensePlate.enabled, DTO uses licensePlate.showLicensePlate
      licensePlate: apiData.licensePlate ? {
        ...apiData.licensePlate,
        showLicensePlate: apiData.licensePlate.showLicensePlate ?? apiData.licensePlate.enabled ?? false,
      } : { showLicensePlate: false, isMandatory: false },
    };

    return model;
  }

  /**
   * Transform model for API: Convert seconds → milliseconds
   * Prepares data to be sent to API in milliseconds format
   * @private
   */
  private transformModelToApi(model: any): any {
    const api = { ...model };

    // Map DTO field names back to API field names
    if (model.logoUrl !== undefined)             { api.logo = model.logoUrl; delete api.logoUrl; }
    if (model.connectedStatus !== undefined)     { api.connected = model.connectedStatus; delete api.connectedStatus; }
    if (model.minimumCostPerMinute !== undefined){ api.minCostPerMinuteThreshold = model.minimumCostPerMinute; delete api.minimumCostPerMinute; }
    if (model.isIncrementalAuth !== undefined)   { api.isSupportedIncrementalAuth = model.isIncrementalAuth; delete api.isIncrementalAuth; }
    if (model.maxTransactionFee !== undefined)   { api.maxTransactionFeePercentage = model.maxTransactionFee; delete api.maxTransactionFee; }

    // Map showLicensePlate → enabled for API
    if (model.licensePlate?.showLicensePlate !== undefined) {
      api.licensePlate = { ...model.licensePlate, enabled: model.licensePlate.showLicensePlate };
      delete api.licensePlate.showLicensePlate;
    }

    // Transform sessionTimeout: seconds → ms
    if (model.sessionTimeout !== undefined) {
      api.sessionTimeout = Math.round(TIME_CONVERSION.SECONDS_TO_MS(model.sessionTimeout));
    }

    // Transform idleScreen.timeout: seconds → ms
    if (model.idleScreen?.timeout !== undefined) {
      if (!api.idleScreen) api.idleScreen = {};
      api.idleScreen.timeout = Math.round(
        TIME_CONVERSION.SECONDS_TO_MS(model.idleScreen.timeout)
      );
    }

    // Transform connectedAction.time: seconds → ms, always include type.
    if (model.connectedAction !== undefined) {
      api.connectedAction = {
        ...api.connectedAction,
        type: model.connectedAction.type,
        ...(model.connectedAction.time !== undefined && model.connectedAction.time >= 0 && {
          time: Math.round(TIME_CONVERSION.SECONDS_TO_MS(model.connectedAction.time)),
        }),
      };
    }

    // Remove undefined values to create clean delta
    return this.removeUndefinedValues(api);
  }

  /**
   * Remove undefined values from object (recursive)
   * Ensures clean delta objects without null/undefined fields
   * @private
   */
  private removeUndefinedValues(obj: any): any {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.removeUndefinedValues(item));
    }

    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined && value !== null) {
        if (typeof value === 'object') {
          const cleanedNested = this.removeUndefinedValues(value);
          if (Object.keys(cleanedNested).length > 0) {
            cleaned[key] = cleanedNested;
          }
        } else {
          cleaned[key] = value;
        }
      }
    }
    return cleaned;
  }
}
